import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/lib/api";
import { weatherInfo } from "@/lib/weatherIcons";
import { MapPin } from "lucide-react";
import TimerButton from "@/components/TimerButton";

function useNow() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export default function ClockWeather() {
  const now = useNow();
  const { data } = useQuery({
    queryKey: ["weather"],
    queryFn: endpoints.weather,
    refetchInterval: 1000 * 60 * 10,
  });

  const current = data?.current || {};
  const daily = data?.daily || {};
  const { Icon, label } = weatherInfo(current.weather_code, current.is_day);

  const timeStr = now.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const dateStr = now.toLocaleDateString([], {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div
      data-testid="clock-weather-card"
      className="wood-panel rounded-[2rem] border p-5 lg:p-6 flex flex-col overflow-hidden"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div
            data-testid="live-clock"
            className="font-serif text-5xl md:text-6xl leading-none tracking-tighter text-[#EAE4DB]"
            style={{ fontWeight: 400 }}
          >
            {timeStr}
          </div>
          <div className="mt-1.5 text-sm md:text-base font-mono uppercase tracking-[0.18em] text-[#A6A098]">
            {dateStr}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <TimerButton inline />
          <div className="flex flex-col items-end">
            <Icon
              className="text-[#D6A85B] pulse-soft"
              size={56}
              strokeWidth={1.25}
            />
            <div className="mt-1 text-[0.7rem] uppercase font-mono tracking-[0.15em] text-[#A6A098]">
              {label}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-end justify-between gap-6">
        <div>
          <div
            data-testid="current-temp"
            className="font-serif text-4xl md:text-5xl text-[#EAE4DB] leading-none"
            style={{ fontWeight: 500 }}
          >
            {current.temperature_2m != null ? Math.round(current.temperature_2m) : "—"}
            <span className="text-xl align-top ml-1 text-[#A6A098]">°F</span>
          </div>
          <div className="mt-1.5 text-xs font-mono uppercase tracking-[0.15em] text-[#A6A098] flex items-center gap-2">
            <MapPin size={12} /> {data?.location || "Redmond, OR"}
          </div>
        </div>
        <div className="text-right text-xs text-[#A6A098]">
          <div>
            Feels{" "}
            <span className="text-[#EAE4DB]">
              {current.apparent_temperature != null
                ? `${Math.round(current.apparent_temperature)}°`
                : "—"}
            </span>
          </div>
          <div>
            Wind{" "}
            <span className="text-[#EAE4DB]">
              {current.wind_speed_10m != null ? `${Math.round(current.wind_speed_10m)} mph` : "—"}
            </span>
          </div>
          <div>
            Humidity{" "}
            <span className="text-[#EAE4DB]">
              {current.relative_humidity_2m != null ? `${current.relative_humidity_2m}%` : "—"}
            </span>
          </div>
        </div>
      </div>

      {/* 7-day forecast */}
      <div
        data-testid="forecast-row"
        className="mt-4 grid grid-cols-7 gap-2 pt-4 border-t border-[#2a1810]"
      >
        {(daily?.time || []).map((d, i) => {
          const info = weatherInfo(daily.weather_code?.[i], 1);
          const day = new Date(d).toLocaleDateString([], { weekday: "short" });
          return (
            <div
              key={d}
              data-testid={`forecast-day-${i}`}
              className="flex flex-col items-center gap-1"
            >
              <div className="text-[0.65rem] font-mono uppercase tracking-[0.15em] text-[#A6A098]">
                {i === 0 ? "Today" : day}
              </div>
              <info.Icon size={22} strokeWidth={1.25} className="text-[#EAE4DB]" />
              <div className="text-xs text-[#EAE4DB] font-medium">
                {Math.round(daily.temperature_2m_max?.[i])}°
              </div>
              <div className="text-[0.7rem] text-[#A6A098]">
                {Math.round(daily.temperature_2m_min?.[i])}°
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
